import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Clock, FileText, Send, MessageSquare, Loader2, RefreshCw, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicQuote() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [approverName, setApproverName] = useState("");
  const [changeMessage, setChangeMessage] = useState("");
  const [isApproving, setIsApproving] = useState(false);
  const [isRequestingChanges, setIsRequestingChanges] = useState(false);
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadQuote();
  }, [token]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/public/quote/${token}`);
      setQuoteData(res.data);
      setError(null);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Failed to load quote";
      if (status === 404) {
        setError({ type: "not_found", message: "Quote not found" });
      } else if (status === 410) {
        setError({ type: "expired", message: "This quote has expired" });
      } else {
        setError({ type: "error", message: detail });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approverName.trim()) {
      toast.error("Please enter your name to approve");
      return;
    }
    
    setIsApproving(true);
    try {
      const res = await axios.post(`${API}/api/public/quote/${token}/approve`, {
        approver_name: approverName.trim(),
      });
      setResult({ type: "approved", data: res.data });
      toast.success("Quote approved successfully!");
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to approve quote";
      toast.error(detail);
    } finally {
      setIsApproving(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!changeMessage.trim()) {
      toast.error("Please enter your message");
      return;
    }
    
    setIsRequestingChanges(true);
    try {
      const res = await axios.post(`${API}/api/public/quote/${token}/request-changes`, {
        message: changeMessage.trim(),
      });
      setResult({ type: "changes_requested", data: res.data });
      toast.success("Change request submitted!");
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to submit request";
      toast.error(detail);
    } finally {
      setIsRequestingChanges(false);
    }
  };

  const formatCurrency = (value) => {
    return `$${parseFloat(value || 0).toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          <p className="mt-2 text-slate-500">Loading quote...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            {error.type === "expired" ? (
              <Clock className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            ) : (
              <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            )}
            <h2 className="text-xl font-semibold mb-2">
              {error.type === "expired" ? "Quote Expired" : "Quote Not Found"}
            </h2>
            <p className="text-slate-500">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already processed states
  if (quoteData?.status === "already_approved") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Quote Already Approved</h2>
            <p className="text-slate-500">{quoteData.message}</p>
            {quoteData.approved_at && (
              <p className="text-sm text-slate-400 mt-2">
                Approved on {formatDate(quoteData.approved_at)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (quoteData?.status === "changes_requested") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Changes Requested</h2>
            <p className="text-slate-500">{quoteData.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (quoteData?.status === "superseded") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="superseded-quote-card">
          <CardContent className="pt-6 text-center">
            <RefreshCw className="w-12 h-12 mx-auto text-blue-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">This Quote Has Been Updated</h2>
            <p className="text-slate-500 mb-5">{quoteData.message}</p>
            {quoteData.latest_url ? (
              <Button
                className="w-full"
                onClick={() => { window.location.href = quoteData.latest_url; }}
                data-testid="view-latest-quote-btn"
              >
                View the latest quote
                {quoteData.latest_version ? ` (V${quoteData.latest_version})` : ""}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <p className="text-sm text-slate-400">
                Please open the most recent quote email we sent you.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Result states (after action)
  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            {result.type === "approved" ? (
              <>
                <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h2 className="text-2xl font-semibold mb-2">Quote Approved!</h2>
                <p className="text-slate-500 mb-4">
                  Thank you, {result.data.approved_by}. Your quote for{" "}
                  <strong>{result.data.job_name}</strong> has been approved.
                </p>
                <p className="text-sm text-slate-400">
                  We will be in touch shortly to proceed with your order.
                </p>
              </>
            ) : (
              <>
                <MessageSquare className="w-16 h-16 mx-auto text-amber-500 mb-4" />
                <h2 className="text-2xl font-semibold mb-2">Request Submitted</h2>
                <p className="text-slate-500 mb-4">
                  Thank you for your feedback. We will review your request and get back to you shortly.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main quote view
  const { branding, quote } = quoteData;
  const displayTotal = quote.subtotal_ex_gst + (quote.design_fee || 0) + (quote.small_order_fee || 0) + (quote.shipping || 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header with branding */}
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={branding.business_name}
                  className="h-12 object-contain"
                />
              ) : (
                <h1 className="text-2xl font-bold text-slate-800">
                  {branding.business_name}
                </h1>
              )}
            </div>
            <div className="text-right text-sm text-slate-500">
              {branding.business_email && <p>{branding.business_email}</p>}
              {branding.business_phone && <p>{branding.business_phone}</p>}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Quote Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-3xl font-bold text-slate-800">QUOTE</h2>
              {quote.version && (
                <span
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold bg-slate-800 text-white"
                  data-testid="public-quote-version"
                >
                  V{quote.version}
                </span>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Valid Until</p>
              <p className="text-lg font-semibold text-slate-700">
                {formatDate(quote.valid_until)}
              </p>
            </div>
          </div>
          <p className="text-xl text-slate-600">{quote.job_name}</p>
          {quoteData.is_update && (
            <div
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800"
              data-testid="quote-updated-note"
            >
              <RefreshCw className="w-4 h-4" />
              Updated quote — this version (V{quote.version}) replaces any earlier quotes we sent.
            </div>
          )}
          {(quote.client_business_name || quote.client_contact_name || quote.client_name) && (
            <p className="text-slate-500 mt-1">
              For: {quote.client_business_name || quote.client_contact_name || quote.client_name}
            </p>
          )}
        </div>

        {/* Line Items */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Quote Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Description</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-600 w-20">Qty</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-slate-600 w-28">Unit Price</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-slate-600 w-28">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.line_items.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-3 px-2">
                        <p className="font-medium text-slate-800">{item.description}</p>
                        {item.decoration && (
                          <p className="text-sm text-slate-500">Decoration: {item.decoration}</p>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center text-slate-600">{item.quantity}</td>
                      <td className="py-3 px-2 text-right text-slate-600">{formatCurrency(item.unit_price)}</td>
                      <td className="py-3 px-2 text-right font-medium text-slate-800">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                  {quote.design_fee > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-2">
                        <p className="font-medium text-slate-800">Design / Artwork Setup</p>
                      </td>
                      <td className="py-3 px-2 text-center text-slate-600">1</td>
                      <td className="py-3 px-2 text-right text-slate-600">{formatCurrency(quote.design_fee)}</td>
                      <td className="py-3 px-2 text-right font-medium text-slate-800">{formatCurrency(quote.design_fee)}</td>
                    </tr>
                  )}
                  {quote.small_order_fee > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-2">
                        <p className="font-medium text-slate-800">Small Order Setup Fee</p>
                      </td>
                      <td className="py-3 px-2 text-center text-slate-600">1</td>
                      <td className="py-3 px-2 text-right text-slate-600">{formatCurrency(quote.small_order_fee)}</td>
                      <td className="py-3 px-2 text-right font-medium text-slate-800">{formatCurrency(quote.small_order_fee)}</td>
                    </tr>
                  )}
                  {quote.shipping > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-2">
                        <p className="font-medium text-slate-800">Shipping & Handling</p>
                      </td>
                      <td className="py-3 px-2 text-center text-slate-600">1</td>
                      <td className="py-3 px-2 text-right text-slate-600">{formatCurrency(quote.shipping)}</td>
                      <td className="py-3 px-2 text-right font-medium text-slate-800">{formatCurrency(quote.shipping)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-6 pt-4 border-t">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(displayTotal)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>GST (10%)</span>
                    <span>{formatCurrency(quote.gst)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-800 pt-2 border-t">
                    <span>TOTAL AUD</span>
                    <span>{formatCurrency(quote.total_inc_gst)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Approval Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Quote Approval
            </CardTitle>
            <CardDescription>
              To approve this quote, please enter your name below and click "Approve Quote".
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!showChangeForm ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="approver_name">Your Name *</Label>
                  <Input
                    id="approver_name"
                    placeholder="Enter your full name"
                    value={approverName}
                    onChange={(e) => setApproverName(e.target.value)}
                    data-testid="approver-name-input"
                  />
                  <p className="text-xs text-slate-500">
                    By typing your name and clicking "Approve Quote", you confirm acceptance of this quote.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleApprove}
                    disabled={isApproving || !approverName.trim()}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="approve-quote-btn"
                  >
                    {isApproving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Approve Quote
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowChangeForm(true)}
                    data-testid="request-changes-toggle-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Request Changes
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="change_message">Your Message *</Label>
                  <Textarea
                    id="change_message"
                    placeholder="Please describe the changes you would like..."
                    value={changeMessage}
                    onChange={(e) => setChangeMessage(e.target.value)}
                    rows={4}
                    data-testid="change-message-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleRequestChanges}
                    disabled={isRequestingChanges || !changeMessage.trim()}
                    variant="outline"
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="submit-changes-btn"
                  >
                    {isRequestingChanges ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowChangeForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <footer className="mt-8 text-center text-sm text-slate-500">
          <p>Prices exclude GST unless stated otherwise.</p>
          <p>Quote valid until {formatDate(quote.valid_until)}.</p>
        </footer>
      </main>
    </div>
  );
}
